import React, { useEffect, useState } from "react";
import Footer from "../Component/Footer";
import Header from "../Component/Header";
import { Users, Target, Heart, ArrowRight, DollarSign, TrendingUp, Building, Shield, Coins, CreditCard, PiggyBank, Briefcase } from "lucide-react";

function Sacco() {
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: Heart,
      title: "Empowering Communities",
      description: "We empower widows, adolescent mothers, youths living with HIV, and other vulnerable community members by extending loans at competitive interest rates.",
      color: "from-purple-500 to-pink-500",
      highlight: "Inclusive Support"
    },
    {
      icon: Briefcase,
      title: "Supporting Small Businesses", 
      description: "Our SACCO supports small businesses and entrepreneurs to thrive, creating sustainable economic opportunities within the community.",
      color: "from-blue-500 to-cyan-500",
      highlight: "Business Growth"
    },
    {
      icon: DollarSign,
      title: "Low-Interest Loans",
      description: "We offer loans with a competitive monthly interest rate of 3% (reducing balance) to our members, ensuring financial inclusivity and affordability.",
      color: "from-green-500 to-emerald-500",
      highlight: "3% Interest Rate"
    }
  ];

  const features = [
    {
      icon: Building,
      title: "Registered SACCO",
      description: "Officially registered in 2021 under P.21585/RCS",
      metric: "2021"
    },
    {
      icon: TrendingUp,
      title: "Economic Development",
      description: "Fostering development and boosting local businesses",
      metric: "100%"
    },
    {
      icon: Users,
      title: "Community Focus",
      description: "Serving Kayunga community members and beneficiaries",
      metric: "All"
    },
    {
      icon: Shield,
      title: "Sustainability",
      description: "Long-term financial stability and growth",
      metric: "Future"
    }
  ];

  const benefits = [
    { icon: PiggyBank, title: "Savings Programs", description: "Secure savings plans with competitive returns for members" },
    { icon: CreditCard, title: "Credit Services", description: "Accessible credit facilities for business and personal needs" },
    { icon: Coins, title: "Financial Literacy", description: "Training and education on financial management and planning" },
    { icon: TrendingUp, title: "Investment Opportunities", description: "Diverse investment options for long-term wealth building" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-blue-50 to-green-50">
      <Header />
      
      {/* Hero Section */}
      <div className="relative overflow-hidden pt-40 pb-20">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-64 h-64 bg-green-200/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-48 h-48 bg-blue-200/30 rounded-full blur-2xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-emerald-200/30 rounded-full blur-xl animate-pulse delay-2000"></div>
          
          {/* Decorative financial icons */}
          <div className="absolute top-1/3 right-1/4 w-96 h-96 opacity-5">
            <svg viewBox="0 0 100 100" className="w-full h-full text-green-600 animate-pulse">
              <circle cx="50" cy="30" r="20" fill="currentColor"/>
              <rect x="30" y="55" width="40" height="30" rx="5" fill="currentColor"/>
              <text x="50" y="75" textAnchor="middle" fontSize="12" fill="white">$</text>
            </svg>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4">
          <div 
            id="hero-content"
            data-animate
            className={`text-center transform transition-all duration-1000 ${
              isVisible['hero-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <Building className="w-4 h-4 mr-2" />
              Registered Financial Cooperative
            </div>
            
            <h1 className="text-4xl md:text-6xl font-black mb-8">
              <span className="bg-gradient-to-r from-green-600 via-blue-600 to-emerald-600 bg-clip-text text-transparent">
                Kayunga Community
              </span>
              <br />
              <span className="text-gray-800">Saving & Credit Cooperative</span>
            </h1>
            
            <div className="max-w-4xl mx-auto">
              <div className="bg-white/80 backdrop-blur-xl rounded-3xl p-8 md:p-12 shadow-xl border border-green-100">
                <p className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-6">
                  Established in <strong className="text-green-700">2021</strong> under registration P.21585/RCS, 
                  our SACCO serves as a sustainability foundation, providing <strong>friendly credit services</strong> 
                  to foster development, boost small businesses, and provide startup capital to mitigate unemployment.
                </p>
                <div className="bg-gradient-to-r from-green-50 to-blue-50 border-l-4 border-green-500 p-6 rounded-r-2xl">
                  <p className="text-lg text-gray-700 font-medium">
                    <strong>Our Mission:</strong> Ensuring steady household income and economic empowerment 
                    for Kayunga community members through accessible financial services.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Key Features Section */}
      <div className="py-20 bg-white/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            id="features-header"
            data-animate
            className={`text-center mb-16 transform transition-all duration-1000 ${
              isVisible['features-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              SACCO <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">Fundamentals</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Built on strong foundations for sustainable community financial development
            </p>
            <div className="w-32 h-1 bg-gradient-to-r from-green-500 to-blue-500 rounded-full mx-auto mt-6"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  id={`feature-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 delay-${index * 200} ${
                    isVisible[`feature-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                >
                  <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border-l-4 border-green-500">
                    <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-center">
                      <div className="text-4xl font-black text-gray-800 mb-2">{feature.metric}</div>
                      <div className="text-lg font-semibold text-green-600 mb-2">{feature.title}</div>
                      <div className="text-sm text-gray-600">{feature.description}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div 
            id="services-header"
            data-animate
            className={`text-center mb-16 transform transition-all duration-1000 ${
              isVisible['services-header'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Our <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">Services</span>
            </h2>
            <p className="text-xl text-gray-600">Comprehensive financial solutions for community empowerment</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div
                  key={index}
                  id={`service-${index}`}
                  data-animate
                  className={`transform transition-all duration-1000 delay-${index * 300} ${
                    isVisible[`service-${index}`] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                >
                  <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 h-full">
                    <div className={`w-20 h-20 bg-gradient-to-r ${service.color} rounded-3xl flex items-center justify-center mb-6`}>
                      <Icon className="w-10 h-10 text-white" />
                    </div>
                    
                    <h3 className="text-2xl font-bold text-gray-800 mb-4">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
                    
                    <div className={`inline-flex items-center bg-gradient-to-r ${service.color} bg-opacity-10 px-4 py-2 rounded-full`}>
                      <div className={`w-3 h-3 bg-gradient-to-r ${service.color} rounded-full mr-3`}></div>
                      <span className="font-semibold text-gray-800">{service.highlight}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="py-20 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Member <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">Benefits</span>
            </h2>
            <p className="text-xl text-gray-600">Comprehensive financial services for our community</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-800 mb-3">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Interest Rate Highlight */}
      <div className="py-16 bg-white/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div 
            id="rate-highlight"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['rate-highlight'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-3xl p-1 shadow-2xl">
              <div className="bg-white rounded-3xl p-8 md:p-12">
                <DollarSign className="w-20 h-20 text-green-600 mx-auto mb-6" />
                <h3 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                  Competitive <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">Interest Rates</span>
                </h3>
                <div className="text-6xl md:text-8xl font-black text-green-600 mb-4">3%</div>
                <p className="text-xl text-gray-700 font-medium mb-4">Monthly Interest Rate (Reducing Balance)</p>
                <p className="text-lg text-gray-600">
                  We offer some of the most competitive rates in the region, ensuring 
                  financial inclusivity and affordability for all our members.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Call to Action Section */}
      <div className="py-20 bg-gradient-to-r from-green-600 via-blue-600 to-emerald-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 text-center">
          <div 
            id="cta-content"
            data-animate
            className={`transform transition-all duration-1000 ${
              isVisible['cta-content'] ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
            }`}
          >
            <div className="inline-flex items-center bg-white/20 backdrop-blur-sm text-white px-6 py-3 rounded-full text-lg font-semibold mb-8">
              <PiggyBank className="w-5 h-5 mr-3" />
              Save • Invest • Grow
            </div>
            
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-8">
              Join Our Financial Community
            </h2>
            <p className="text-xl text-white/90 mb-12 max-w-3xl mx-auto">
              Become a member of Kayunga Community SACCO and access affordable credit, 
              secure savings, and comprehensive financial services. Together, we build 
              stronger communities through financial empowerment.
            </p>
            
            <button 
              onClick={() => window.location.href = '/contact-us'}
              className="bg-white text-green-600 px-10 py-4 rounded-2xl font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 mx-auto"
            >
              Become a Member <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Sacco;